import QRCode from "qrcode";

declare const window: Window & {
  __BB_CONFIG__?: { apiUrl?: string; apiKey?: string; viewerBaseUrl?: string };
};

const API_URL = () => (window.__BB_CONFIG__?.apiUrl || "").replace(/\/+$/, "");
const API_KEY = () => window.__BB_CONFIG__?.apiKey || "";
const VIEWER_BASE_URL = () => window.__BB_CONFIG__?.viewerBaseUrl || "https://d2q54yq22iw6x8.cloudfront.net/v";

// ── Court config ─────────────────────────────────────────────────────
interface Court {
  label: string;
  deviceId: string;
}

export const COURTS: Court[] = [
  { label: 'Court 1', deviceId: 'ec2-gpu-01' },
  { label: 'Court 2', deviceId: 'ec2-gpu-02' },
];

export const STORAGE_KEY = 'bb_court';
const ROLE_STORAGE_KEY = 'bb_role'; // "ec2-gpu-01" | "ec2-gpu-02" | "past-sessions"

export function getSelectedDeviceId(): string {
  const role = localStorage.getItem(ROLE_STORAGE_KEY) || "";
  const court = COURTS.find(c => c.deviceId === role);
  return court ? court.deviceId : COURTS[0].deviceId;
}

// ── API calls ────────────────────────────────────────────────────────
async function apiCall(method: string, path: string, body?: unknown): Promise<unknown> {
  const opts: RequestInit = {
    method,
    headers: { "Content-Type": "application/json", "X-Api-Key": API_KEY() },
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_URL()}${path}`, opts);
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

// ── Toast ────────────────────────────────────────────────────────────
let toastTimer: ReturnType<typeof setTimeout>;
function showToast(msg: string): void {
  const el = document.getElementById("toast")!;
  el.textContent = msg;
  el.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove("show"), 3500);
}

// ── DOM refs ─────────────────────────────────────────────────────────
const $ = (id: string) => document.getElementById(id)!;

let sessionActive = false;
let pollInterval: ReturnType<typeof setInterval> | null = null;
let startExtendedSec = 0; // total seconds extended this session

// ── Session offset settings ─────────────────────────────────────────
const OFFSET_KEY = "bb_time_offset_s";

function getTimeOffset(): number {
  return parseInt(localStorage.getItem(OFFSET_KEY) || "0", 10) || 0;
}

function updateOffsetDisplay(): void {
  const val = ($("setting-time-offset") as HTMLInputElement).value;
  const n = parseInt(val, 10) || 0;
  $("offset-value").textContent = `${n > 0 ? "+" : ""}${n}s`;
  $("offset-value").style.color = n === 0 ? "var(--text)" : n < 0 ? "var(--cyan)" : "var(--accent)";
}

function openSettings(): void {
  ($("setting-time-offset") as HTMLInputElement).value = String(getTimeOffset());
  updateOffsetDisplay();
  $("settings-modal").classList.remove("hidden");
}

function saveSettings(): void {
  const val = parseInt(($("setting-time-offset") as HTMLInputElement).value, 10) || 0;
  localStorage.setItem(OFFSET_KEY, String(val));
  $("settings-modal").classList.add("hidden");
  showToast(`Time offset saved: ${val > 0 ? "+" : ""}${val}s`);
}

function closeSettings(): void {
  $("settings-modal").classList.add("hidden");
}

// ── Start session ────────────────────────────────────────────────────
async function startSession(): Promise<void> {
  try {
    $("start-btn").classList.add("disabled");
    await apiCall("POST", "/session", { device_id: getSelectedDeviceId(), time_offset_s: getTimeOffset() });
    poll();
  } catch (e: unknown) {
    showToast((e as Error).message);
    $("start-btn").classList.remove("disabled");
  }
}

// ── Stop session ─────────────────────────────────────────────────────
async function stopSession(): Promise<void> {
  try {
    await apiCall("DELETE", "/session", { device_id: getSelectedDeviceId(), time_offset_s: getTimeOffset() });
    poll();
  } catch (e: unknown) {
    showToast((e as Error).message);
  }
}

// ── Poll loop ────────────────────────────────────────────────────────
interface SessionData {
  live?: {
    session_active?: boolean;
    auto_stopped?: boolean;
    session_id?: string;
    elapsed_s?: number;
    shots?: number;
    made?: number;
  };
  lastCompleted?: {
    totalAttempts?: number;
    totalMakes?: number;
    shootingPct?: number;
    bestStreak?: number;
  };
}

async function poll(): Promise<void> {
  try {
    const data = (await apiCall("GET", `/session?device_id=${getSelectedDeviceId()}`)) as SessionData;
    if (!data) return;
    if (data.live?.auto_stopped) {
      showToast("Session auto-stopped after 5 minutes");
    }
    updateUI(data);
    $("conn-dot").classList.add("on");
    $("conn-label").textContent = "connected";
  } catch {
    $("conn-dot").classList.remove("on");
    $("conn-label").textContent = "error";
  }
}

function updateUI(data: SessionData): void {
  const live = data.live || {};
  const last = data.lastCompleted;
  const active = live.session_active === true;

  if (active !== sessionActive) {
    sessionActive = active;
    if (active) {
      $("start-zone").classList.add("hidden");
      $("stop-zone").classList.add("active");
      $("status-tag").className = "status-tag active";
      $("status-tag").textContent = "ACTIVE";
      startExtendedSec = 0;
      updateExtendBtn();
    } else {
      $("start-zone").classList.remove("hidden");
      $("stop-zone").classList.remove("active");
      $("start-btn").classList.remove("disabled");
      $("status-tag").className = "status-tag idle";
      $("status-tag").textContent = "IDLE";
      startExtendedSec = 0;
      updateExtendBtn();
      resetSlider();
    }
  }

  $("session-num").textContent = active ? (live.session_id ?? "--").slice(0, 4) : "--";

  const elapsed = live.elapsed_s ?? 0;
  if (active) {
    const mins = Math.floor(elapsed / 60);
    const secs = Math.floor(elapsed % 60);
    $("countdown-time").textContent = `${mins}:${secs.toString().padStart(2, "0")}`;
    $("countdown-time").className = "countdown-time";
    $("countdown-extend-label").textContent = startExtendedSec > 0 ? `Start extended by ${startExtendedSec}s` : "";
    const pct = Math.min(100, (elapsed / 300) * 100);
    $("countdown-fill").style.width = pct + "%";
    $("countdown-fill").className = "countdown-fill";
  } else {
    $("countdown-time").textContent = "--";
    $("countdown-time").className = "countdown-time";
    $("countdown-extend-label").textContent = "";
    ($("countdown-fill") as HTMLElement).style.width = "0%";
  }

  const shots = live.shots ?? 0;
  const made = live.made ?? 0;
  const pctVal = shots > 0 ? Math.round((made / shots) * 100) : 0;
  $("s-shots").textContent = String(shots);
  $("s-made").textContent = String(made);
  $("s-pct").textContent = pctVal + "%";
  $("s-streak").textContent = "--";

  for (let i = 1; i <= 3; i++) {
    const dot = $("dot-" + i);
    if (i <= made) {
      dot.className = "shot-dot make";
      dot.textContent = "\u2713";
    } else if (i <= shots) {
      dot.className = "shot-dot miss";
      dot.textContent = "\u2717";
    } else {
      dot.className = "shot-dot pending";
      dot.textContent = String(i);
    }
  }

  if (last) {
    $("last-session").style.display = "";
    $("ls-attempts").textContent = String(last.totalAttempts ?? "--");
    $("ls-makes").textContent = String(last.totalMakes ?? "--");
    $("ls-pct").textContent = last.shootingPct != null ? Math.round(last.shootingPct) + "%" : "--";
    $("ls-best").textContent = String(last.bestStreak ?? "--");
  }
}

// ── Slide-to-stop logic ──────────────────────────────────────────────
let sliding = false;
let slideStartX = 0;
let thumbLeft = 4;
const THUMB_SIZE = 56;
const PADDING = 4;

function getTrackWidth(): number {
  return $("slide-track").getBoundingClientRect().width;
}
function getMaxLeft(): number {
  return getTrackWidth() - THUMB_SIZE - PADDING * 2;
}

function onSlideStart(e: MouseEvent | TouchEvent): void {
  e.preventDefault();
  sliding = true;
  $("slide-thumb").classList.add("dragging");
  const touch = "touches" in e ? e.touches[0] : e;
  slideStartX = touch.clientX - thumbLeft;
}

function onSlideMove(e: MouseEvent | TouchEvent): void {
  if (!sliding) return;
  e.preventDefault();
  const touch = "touches" in e ? e.touches[0] : e;
  let newLeft = touch.clientX - slideStartX;
  newLeft = Math.max(PADDING, Math.min(newLeft, getMaxLeft()));
  thumbLeft = newLeft;
  ($("slide-thumb") as HTMLElement).style.left = newLeft + "px";
  const pct = (newLeft - PADDING) / (getMaxLeft() - PADDING);
  ($("slide-track").querySelector(".slide-label") as HTMLElement).style.opacity = String(1 - pct);
}

function onSlideEnd(): void {
  if (!sliding) return;
  sliding = false;
  $("slide-thumb").classList.remove("dragging");
  const pct = (thumbLeft - PADDING) / (getMaxLeft() - PADDING);
  if (pct > 0.85) stopSession();
  resetSlider();
}

function resetSlider(): void {
  thumbLeft = PADDING;
  ($("slide-thumb") as HTMLElement).style.left = PADDING + "px";
  ($("slide-track").querySelector(".slide-label") as HTMLElement).style.opacity = "1";
}

function initSlider(): void {
  $("slide-thumb").addEventListener("mousedown", onSlideStart);
  $("slide-thumb").addEventListener("touchstart", onSlideStart, { passive: false });
  document.addEventListener("mousemove", onSlideMove);
  document.addEventListener("touchmove", onSlideMove, { passive: false });
  document.addEventListener("mouseup", onSlideEnd);
  document.addEventListener("touchend", onSlideEnd);
}

// ── Extend session start ────────────────────────────────────────────

const MAX_EXTEND_SEC = 60;

function updateExtendBtn(): void {
  const btn = $("extend-btn") as HTMLButtonElement;
  const info = $("extend-info");
  if (startExtendedSec >= MAX_EXTEND_SEC) {
    btn.disabled = true;
    btn.textContent = "max extended";
  } else {
    btn.disabled = false;
    btn.textContent = "+10s to start";
  }
  info.textContent = startExtendedSec > 0 ? `Start extended by ${startExtendedSec}s` : "";
}

function showConfirmModal(text: string): Promise<boolean> {
  return new Promise((resolve) => {
    const modal = $("confirm-modal");
    $("confirm-text").textContent = text;
    modal.classList.remove("hidden");

    function cleanup(result: boolean) {
      modal.classList.add("hidden");
      $("confirm-ok").removeEventListener("click", onOk);
      $("confirm-cancel").removeEventListener("click", onCancel);
      resolve(result);
    }
    function onOk() { cleanup(true); }
    function onCancel() { cleanup(false); }

    $("confirm-ok").addEventListener("click", onOk);
    $("confirm-cancel").addEventListener("click", onCancel);
  });
}

async function extendStart(): Promise<void> {
  if (startExtendedSec >= MAX_EXTEND_SEC) return;

  const msg = startExtendedSec === 0
    ? "This will extend the session start time by 10 seconds. Continue?"
    : `You have already extended the session start time by ${startExtendedSec} seconds. Are you sure you want to extend it by another 10 seconds?`;

  const confirmed = await showConfirmModal(msg);
  if (!confirmed) return;

  try {
    await apiCall("PATCH", "/session", {
      device_id: getSelectedDeviceId(),
      extend_seconds: 10,
    });
    startExtendedSec += 10;
    updateExtendBtn();
    showToast("Start extended by 10s");
  } catch (e: unknown) {
    showToast((e as Error).message);
  }
}

// ── Role selection ──────────────────────────────────────────────────

function selectRole(role: string): void {
  localStorage.setItem(ROLE_STORAGE_KEY, role);
  $("role-screen").classList.add("hidden");
  $("operator-view").classList.remove("hidden");

  // Update role badge
  const badge = $("role-badge");
  if (role === "past-sessions") {
    badge.className = "role-badge past-sessions";
    badge.textContent = "Past Sessions";
    $("tab-control").classList.add("hidden");
    $("tab-past-sessions").classList.remove("hidden");
    stopPolling();
    startClipsPolling();
  } else {
    const court = COURTS.find(c => c.deviceId === role);
    const idx = COURTS.indexOf(court!);
    badge.className = `role-badge court-${idx + 1}`;
    badge.textContent = court?.label || role;
    $("tab-control").classList.remove("hidden");
    $("tab-past-sessions").classList.add("hidden");
    stopClipsPolling();
    startPolling();
  }
}

function showRoleScreen(): void {
  stopPolling();
  stopClipsPolling();
  $("role-screen").classList.remove("hidden");
  $("operator-view").classList.add("hidden");
}

function restoreRole(): boolean {
  const saved = localStorage.getItem(ROLE_STORAGE_KEY);
  if (saved) {
    selectRole(saved);
    return true;
  }
  return false;
}

// ── Polling ──────────────────────────────────────────────────────────
export function startPolling(): void {
  poll();
  if (pollInterval) clearInterval(pollInterval);
  pollInterval = setInterval(poll, 1500);
}

export function stopPolling(): void {
  if (pollInterval) clearInterval(pollInterval);
}

// ── Past Sessions / Clips ───────────────────────────────────────────

interface ClipRecord {
  sessionId: string;
  deviceId: string;
  playerName: string | null;
  playerNumber: string | null;
  durationSec: number | null;
  epochMs: number;
  clipUrl: string | null;
}

let clipsCache: ClipRecord[] | null = null;
let clipsPollInterval: ReturnType<typeof setInterval> | null = null;
let clipsFilter: string = "all"; // "all" | device ID

function courtLabel(deviceId: string): string {
  return COURTS.find(c => c.deviceId === deviceId)?.label || deviceId;
}

function courtClass(deviceId: string): string {
  const idx = COURTS.findIndex(c => c.deviceId === deviceId);
  return idx >= 0 ? `court-${idx + 1}` : "";
}

function timeAgo(epochMs: number): string {
  const diff = Date.now() - epochMs;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function filteredClips(): ClipRecord[] {
  if (!clipsCache) return [];
  if (clipsFilter === "all") return clipsCache;
  return clipsCache.filter(c => c.deviceId === clipsFilter);
}

async function loadClips(force = false): Promise<void> {
  const container = $("clips-list");
  if (clipsCache && !force) {
    renderClips(filteredClips(), container);
    return;
  }
  if (!clipsCache) {
    container.innerHTML = '<div class="clips-loading">Loading...</div>';
  }
  try {
    const data = (await apiCall("GET", "/session/clips?limit=50")) as { clips: ClipRecord[] };
    clipsCache = data.clips;
    renderClips(filteredClips(), container);
    $("conn-dot").classList.add("on");
    $("conn-label").textContent = "connected";
  } catch (e: unknown) {
    if (!clipsCache) {
      container.innerHTML = '<div class="clips-empty">Failed to load sessions</div>';
    }
    $("conn-dot").classList.remove("on");
    $("conn-label").textContent = "error";
    showToast((e as Error).message);
  }
}

function renderClips(clips: ClipRecord[], container: HTMLElement): void {
  if (clips.length === 0) {
    container.innerHTML = '<div class="clips-empty">No completed sessions yet</div>';
    return;
  }
  container.innerHTML = "";
  for (const clip of clips) {
    const card = document.createElement("div");
    card.className = `clip-card ${courtClass(clip.deviceId)}`;

    const viewerUrl = `${VIEWER_BASE_URL()}/${clip.sessionId}`;
    const date = new Date(clip.epochMs);
    const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const timeStr = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    const durStr = clip.durationSec ? formatDuration(clip.durationSec) : "--";

    card.innerHTML = `
      <div class="clip-top">
        <span class="clip-court">${courtLabel(clip.deviceId)}</span>
        <span class="clip-ago">${timeAgo(clip.epochMs)}</span>
      </div>
      <div class="clip-meta">${dateStr} ${timeStr} &middot; ${durStr}</div>
    `;

    const idx = clips.indexOf(clip);
    card.addEventListener("click", () => showQrModal(idx));
    container.appendChild(card);
  }
}

// ── QR fullscreen modal ─────────────────────────────────────────────

function showQrModal(index: number): void {
  const clips = filteredClips();
  if (!clips || !clips[index]) return;

  // Remove existing modal if any
  document.getElementById("qr-modal")?.remove();

  const clip = clips[index];
  const viewerUrl = `${VIEWER_BASE_URL()}/${clip.sessionId}`;
  const court = courtLabel(clip.deviceId);
  const courtCls = courtClass(clip.deviceId);
  const date = new Date(clip.epochMs);
  const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const timeStr = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const modal = document.createElement("div");
  modal.id = "qr-modal";
  modal.className = `qr-modal ${courtCls}`;

  const videoHtml = clip.clipUrl
    ? `<video class="qr-modal-video" src="${clip.clipUrl}" autoplay loop muted playsinline></video>`
    : "";

  // Newer = lower index (list is newest-first), older = higher index
  const hasNewer = index > 0;
  const hasOlder = index < clips.length - 1;

  modal.innerHTML = `
    <div class="qr-modal-content">
      <div class="qr-modal-header">
        <div class="qr-modal-court">${court}</div>
        <div class="qr-modal-date">${dateStr} ${timeStr}</div>
      </div>
      <div class="qr-modal-body">
        <button class="qr-modal-nav prev ${hasNewer ? "" : "hidden-nav"}" aria-label="Newer session">
          <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        ${videoHtml}
        <div class="qr-modal-right">
          <div class="qr-modal-qr"></div>
          <div class="qr-modal-hint">scan to view clip</div>
        </div>
        <button class="qr-modal-nav next ${hasOlder ? "" : "hidden-nav"}" aria-label="Older session">
          <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
      <button class="qr-modal-close">Close</button>
    </div>
  `;

  // Close button
  modal.querySelector(".qr-modal-close")!.addEventListener("click", (e) => { e.stopPropagation(); modal.remove(); });

  // Nav buttons
  const prevBtn = modal.querySelector(".qr-modal-nav.prev") as HTMLElement;
  const nextBtn = modal.querySelector(".qr-modal-nav.next") as HTMLElement;
  prevBtn.addEventListener("click", (e) => { e.stopPropagation(); showQrModal(index - 1); });
  nextBtn.addEventListener("click", (e) => { e.stopPropagation(); showQrModal(index + 1); });

  // Close on background click
  modal.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target === modal || target.classList.contains("qr-modal-content") || target.classList.contains("qr-modal-header")) {
      modal.remove();
    }
  });

  document.body.appendChild(modal);

  const qrDiv = modal.querySelector(".qr-modal-qr") as HTMLElement;
  QRCode.toDataURL(viewerUrl, { width: 400, margin: 1 }).then((dataUrl: string) => {
    const img = document.createElement("img");
    img.src = dataUrl;
    img.alt = "QR code";
    qrDiv.appendChild(img);
  });
}

function startClipsPolling(): void {
  loadClips(true);
  if (clipsPollInterval) clearInterval(clipsPollInterval);
  clipsPollInterval = setInterval(() => loadClips(true), 15000);
}

function stopClipsPolling(): void {
  if (clipsPollInterval) clearInterval(clipsPollInterval);
  clipsPollInterval = null;
}

// ── Init ────────────────────────────────────────────────────────────

export function initApp(): void {
  $("start-btn").addEventListener("click", startSession);
  $("extend-btn").addEventListener("click", extendStart);
  $("settings-btn").addEventListener("click", openSettings);
  $("settings-save").addEventListener("click", saveSettings);
  $("settings-cancel").addEventListener("click", closeSettings);
  $("setting-time-offset").addEventListener("input", updateOffsetDisplay);
  initSlider();

  // Role selection cards
  document.querySelectorAll(".role-card").forEach((card) => {
    card.addEventListener("click", () => {
      const role = (card as HTMLElement).dataset.role;
      if (role) selectRole(role);
    });
  });

  // Switch role button
  $("switch-role-btn").addEventListener("click", showRoleScreen);

  // Refresh clips button
  $("refresh-clips-btn").addEventListener("click", (e) => {
    e.stopPropagation();
    clipsCache = null;
    loadClips(true);
  });

  // Court filter buttons
  const filterContainer = $("court-filter");
  filterContainer.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest(".filter-btn") as HTMLElement | null;
    if (!btn) return;
    clipsFilter = btn.dataset.filter || "all";
    filterContainer.querySelectorAll(".filter-btn").forEach(b => b.classList.remove("active"));
    btn.classList.add("active");
    renderClips(filteredClips(), $("clips-list"));
  });

  // Restore saved role or show selection
  restoreRole();
}
